import { useFindCustomGeoJsonById } from "@/api/hooks/customGeojson";
import { groundStyles } from "@/constants/maptilerStyleOptions";
import type { LayerUnion, LayerUnionWithAll } from "@/layers";
import { type IsoBandsLayer, readIsoLayerCustomOptions } from "@/layers/geojson/isoLayerShared";
import {
  getBinaryAnomalyDefinition,
  getBinaryAnomalyLayerLabel,
  isBinaryAnomalyLayer,
} from "@/layers/png/binary/TemperatureAnomalyPoc";
import type { AviationTypes } from "@/layers/utility/aviationType";
import { allAviationProperties } from "@/layers/utility/aviationValues";
import { LayerKindName, allLayerModes } from "@/layers/utility/createLayerObject";
import { LayerConfig } from "@/overlay/components";
import { ComponentSwitcher } from "@/overlay/components/CommonParts/ComponentSwitcher";
import { DeleteConfirmationTrigger } from "@/overlay/components/CommonParts/DeleteConfirmationTrigger";
import { ToggleLegend } from "@/overlay/components/CommonParts/ToggleLegend";
import { Button } from "@/overlay/components/CommonParts/buttons";
import { EditableLayerLabel, type LabelledLayer } from "@/overlay/components/LayerStackWindow/EditableLayerLabel";
import { layerToUpdateControllerMapping } from "@/overlay/components/SmartForm/LayerForms/LayerFormMapping";
import { v2LayerUpdateFormSupported } from "@/overlay/components/SmartForm/LayerForms/supportCheckerLayer";
import { setLayersProps } from "@/reducer/MapsState";
import type { MapId } from "@/types/type-utils";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type {
  AlertLayer,
  AviationLayer,
  BackgroundMapLayer,
  BarbsLayer,
  CartographicMapOutput,
  CustomGeoJSONLayerOutput,
  DrawingLayer,
  EarthquakeLayer,
  GenericPoiLayer,
  IsoLinesLayer,
  NwaLayer,
  PortfolioEnergyPoiLayerOutput,
  ThunderstormLayer,
  TropicalCycloneLayer,
  WmsLayer,
} from "@mm/metx-workbench.meteomatics.com";
import { useState } from "react";
import { useDispatch } from "react-redux";
import "./style.scss";

const WAVE_BARBS_PARAMETER_PREFIXES = [
  "ocean_current_speed",
  "stokes_drift_speed",
  "mean_direction_total_swell",
  "mean_wave_direction",
];

function getBarbsLayerModeName(layer: BarbsLayer): string {
  return WAVE_BARBS_PARAMETER_PREFIXES.some((prefix) => layer.parameter_unit.startsWith(prefix))
    ? t`Wave Barbs`
    : t`Wind Barbs`;
}

export function LayerExpansionHeader(props: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  layer: LayerUnionWithAll;
  mapId: MapId;
}) {
  const dispatch = useDispatch();

  const mode = allLayerModes.find((l) => l.kind === props.layer.kind);

  if (!mode) {
    return <></>;
  }

  const modeName = isBinaryAnomalyLayer(props.layer)
    ? t`Anomaly`
    : props.layer.kind === LayerKindName.BarbsLayerDescription
      ? getBarbsLayerModeName(props.layer as BarbsLayer)
      : mode.name();
  const toggle = () => props.setIsOpen(!props.isOpen);

  function changeProps(propsValue: Partial<LayerUnion>) {
    dispatch(setLayersProps({ layerId: props.layer.id, id: props.mapId.map, props: propsValue }));
  }

  function editableLabel(
    layer: LabelledLayer,
    defaultLabel: string,
    { parameter = false }: { parameter?: boolean } = {},
  ) {
    return (
      <span
        className={classNames(
          "layer-label__kindName",
          parameter && "layer-label__kindName--parameter",
          "layer-label__kindName--editable",
        )}
      >
        <EditableLayerLabel
          defaultLabel={defaultLabel}
          layer={layer}
          onSave={(label) => changeProps({ label })}
          onSingleClick={toggle}
        />
      </span>
    );
  }

  function editableParameterLabel(layer: LabelledLayer & { parameter_unit: string }) {
    return editableLabel(layer, layer.parameter_unit, { parameter: true });
  }

  let label: JSX.Element;
  let element_color = "#000000";
  switch (props.layer.kind) {
    case LayerKindName.BackgroundMapDescription: {
      const backgroundLayer = props.layer as BackgroundMapLayer;
      const groundStyle = groundStyles.find((styleLayer) => styleLayer.id === backgroundLayer.style);
      label = editableLabel(backgroundLayer, groundStyle?.label() ?? backgroundLayer.style);
      break;
    }
    case LayerKindName.CustomGeoJSONLayerDescription: {
      const customGeoJsonLayer = props.layer as CustomGeoJSONLayerOutput;
      element_color = customGeoJsonLayer.custom_options.line_color
        ? customGeoJsonLayer.custom_options.line_color
        : "#000000";
      label = (
        <span className="layer-label__kindName">
          <GeoJSONFileName userFileId={customGeoJsonLayer.user_file_id ?? -1} />
        </span>
      );
      break;
    }
    case LayerKindName.AviationLayerDescription: {
      const dataLayer = props.layer as AviationLayer;
      label = editableLabel(
        dataLayer,
        `${t`Aviation Data`} ${allAviationProperties[dataLayer.aviation_type as AviationTypes]?.name() ?? ""}`.trim(),
        { parameter: true },
      );
      break;
    }
    case LayerKindName.GenericPoiLayerDescription: {
      const dataLayer = props.layer as GenericPoiLayer;
      element_color = dataLayer.poiOptions.item.color;
      label = editableLabel(dataLayer, t`Icon/Text`, { parameter: true });
      break;
    }
    case LayerKindName.PortfolioEnergyPoiLayerDescription: {
      const dataLayer = props.layer as PortfolioEnergyPoiLayerOutput;
      label = editableLabel(dataLayer, modeName);
      break;
    }
    case LayerKindName.TropicalCycloneLayerDescription: {
      const dataLayer = props.layer as TropicalCycloneLayer;
      label = editableLabel(dataLayer, dataLayer.aviation_type.replace("tropical_cyclone_", ""), { parameter: true });
      break;
    }
    case LayerKindName.WindAnimationLayerDescription: {
      label = editableLabel(props.layer, t`Wind Animation`, { parameter: true });
      break;
    }
    case LayerKindName.WmsLayerDescription: {
      const dataLayer = props.layer as WmsLayer;
      const anomalyDefinition = getBinaryAnomalyDefinition(dataLayer);
      label = anomalyDefinition
        ? editableLabel(dataLayer, getBinaryAnomalyLayerLabel(anomalyDefinition), { parameter: true })
        : editableParameterLabel(dataLayer);
      break;
    }
    case LayerKindName.BarbsLayerDescription: {
      const dataLayer = props.layer as BarbsLayer;
      element_color = dataLayer.element_color;
      label = editableParameterLabel(dataLayer);
      break;
    }
    case LayerKindName.IsoLinesLayerDescription: {
      const dataLayer = props.layer as IsoLinesLayer;
      element_color = dataLayer.line_color;
      label = editableParameterLabel(dataLayer);
      break;
    }
    case LayerKindName.IsoBandsLayerDescription: {
      const dataLayer = props.layer as IsoBandsLayer;
      const options = readIsoLayerCustomOptions(dataLayer.custom_options);
      element_color = options.fill_color || "#000000";
      label = editableParameterLabel(dataLayer);
      break;
    }
    case LayerKindName.JetStreamsLayerDescription: {
      // biome-ignore lint/complexity/noUselessFragments: The label is conditionally set to an empty fragment, which is not useless in this context.
      label = <></>;
      break;
    }
    default: {
      const dataLayer = props.layer as Exclude<
        LayerUnion,
        | AlertLayer
        | BackgroundMapLayer
        | PortfolioEnergyPoiLayerOutput
        | AviationLayer
        | TropicalCycloneLayer
        | CustomGeoJSONLayerOutput
        | DrawingLayer
        | NwaLayer
        | EarthquakeLayer
        | ThunderstormLayer
      >;
      label = editableParameterLabel(dataLayer);
      break;
    }
  }

  return (
    <div className={`expansion-panel__header ${!props.layer.show ? "hidden" : ""}`} data-testid={props.layer.kind}>
      <div
        data-testid="layer-expansion-toggle"
        className="expansion-panel__header__layer-info"
        onClick={toggle}
        data-dnd-handle
      >
        <span className="expansion-panel__chevron">
          <span className="material-icons">{props.isOpen ? "expand_less" : "expand_more"}</span>
        </span>
        <span className="layer-label">
          <span className="layer-tag__container">
            {/* Quick workaround while we have separate isolines for ensembles. TODO Cleanup when complete renaming will be made */}
            {props.layer.experimental && props.layer.kind !== "IsoLinesLayerDescription" && (
              <span className="tag secondary">{t`Experimental`}</span>
            )}
            <span className="layer-label__kindName tag">
              {props.layer.experimental && props.layer.kind === "IsoLinesLayerDescription"
                ? t`Isolines Ensemble`
                : modeName}
              {element_color !== "#000000" ? (
                <span className="tag-param-color" style={{ backgroundColor: element_color }} />
              ) : null}
            </span>
          </span>
          <span className="layer-label__kindName">{label}</span>
        </span>
      </div>
      <div className="expansion-panel__header__right-control">
        {(props.layer as WmsLayer).legend_visible !== undefined && props.layer.show && (
          <mm-tooltip-wrapper
            tail={true}
            position="top"
            tooltip-text={(props.layer as WmsLayer).legend_visible ? t`Hide legend` : t`Show legend`}
          >
            <ToggleLegend
              id={`layer_${props.layer.id}`}
              legendVisible={(props.layer as WmsLayer).legend_visible}
              onToggle={(enabled) => changeProps({ legend_visible: enabled })}
            />
          </mm-tooltip-wrapper>
        )}
        <mm-tooltip-wrapper tail={true} position="top" tooltip-text={!props.layer.show ? t`Show` : t`Hide`}>
          <Button
            icon={props.layer.show ? "visibility" : "visibility_off"}
            onClick={(_event) => changeProps({ show: !props.layer.show })}
          />
        </mm-tooltip-wrapper>
        <mm-tooltip-wrapper tail={true} position="top-left" tooltip-text={t`Delete`}>
          <DeleteConfirmationTrigger
            element={{
              type: "layer",
              tabId: props.mapId.tab,
              item: props.layer,
              label: t`Do you want to delete layer "${modeName}"?`,
            }}
          >
            <Button data-testid="layer-delete-button" icon="delete" />
          </DeleteConfirmationTrigger>
        </mm-tooltip-wrapper>
      </div>
    </div>
  );
}

export function LayerExpansionPanel(props: {
  layer: LayerUnionWithAll;
  mapId: MapId;
  cartographicMap: CartographicMapOutput;
}) {
  const [isOpen, setIsOpen] = useState(false);
  // TODO: split into components here is weird. there is a div called `expansion-panel__header` with buttons
  // that contains a LayerExpansionHeader component, that just contains a string. The div and buttons should
  // clearly be part of the LayerExpansionHeader component.
  return (
    <div
      className={classNames("expansion-panel box-section--breakout actionable", isOpen && "expansion-panel--expanded")}
      key={props.layer.id}
    >
      <div className="box-section--breakout">
        <LayerExpansionHeader {...props} isOpen={isOpen} setIsOpen={setIsOpen} />
        {isOpen && (
          <div>
            <div
              className="expansion-panel__content"
              // Prevent the drag event to bubble up to the SortableList so the dragging event doesn't fire on the config panel.
              draggable={true}
              onDragStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {v2LayerUpdateFormSupported(props.layer) ? (
                <ComponentSwitcher
                  key={props.layer.id}
                  componentProps={props}
                  componentMapping={layerToUpdateControllerMapping}
                />
              ) : (
                <LayerConfig key={props.layer.id} {...props} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function GeoJSONFileName({ userFileId }: { userFileId: number }) {
  const { data, isLoading } = useFindCustomGeoJsonById(userFileId);

  if (isLoading) {
    return <></>;
  }

  if (!data) {
    return <Trans>File not found</Trans>;
  }

  return <>{data.label ? data.label : data.id}</>;
}
